/* istanbul ignore file */
import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useContext,
} from "react";
import { Rapport } from "../../.generated/rapportage/rapportagetypes";
import { getRapportage } from "../../infra/rapportage-hoofdstukken-api";
import { usePagedReady } from "../../contexts/paged-ready-context";
import { useParams } from "react-router-dom";
import { SettingsContext } from "../../shared/utils/settings-context";
import { rapportComponentMap, componentOrder } from "./rapport-component-map";
import { useRapportContext } from "../hoofdstukonderdelen/context/RapportContext";
import { useChapter } from "../../contexts/chapter-context";

export const RapportageOnderdelen: React.FC = () => {
  const params = useParams() as {
    vestigingId?: string;
    adviesdossierId?: string;
    rapportId?: string;
  };
  const settings = useContext(SettingsContext);
  const chapters = useChapter();
  const { setPagedReady } = usePagedReady();
  const {
    setLogo,
    setFooterText,
    setHasFrontPage,
    setHasBijlagen,
    hasBijlagen,
  } = useRapportContext();
  const [bearerToken] = useState(() =>
    new URLSearchParams(window.location.search).get("bearer")
  );
  const [rapportage, setRapportage] = useState<Rapport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const logoSetRef = useRef<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchHoofdstukken = async (): Promise<void> => {
      try {
        if (!bearerToken) {
          setError("Benodigde instellingen ontbreken.");
          return;
        }

        if (
          !params.vestigingId ||
          !params.adviesdossierId ||
          !params.rapportId
        ) {
          setError("Benodigde parameters ontbreken.");
          return;
        }

        const result = await getRapportage(
          settings,
          bearerToken,
          params.vestigingId,
          params.adviesdossierId,
          params.rapportId,
          controller.signal
        );

        /* istanbul ignore else */
        if (result) {
          chapters.setPrefix(result.inhoudsopgaveTonen);
          setHasBijlagen(
            !!result.toelichtingAdviesAlgemeenHoofdstuk ||
              !!result.toelichtingVerantwoordeWoonlastenHoofdstuk ||
              !!result.toelichtingVerduurzamingHoofdstuk ||
              !!result.toelichtingHypotheekvormHoofdstuk ||
              !!result.toelichtingUwHypotheek ||
              !!result.toelichtingVerantwoordeWoonlastenHoofdstuk ||
              !!result.toelichtingSocialeVoorzieningen ||
              !!result.disclaimer ||
              !!result.toelichtingHypotheekvormen ||
              !!result.klantProfielAlgemeen ||
              !!result.klantprofielHypotheekAlgemeen ||
              !!result.klantProfiel
          );
          setRapportage(result);
        }
      } catch (err) {
        console.error(err);
        setError(
          "Er is een fout opgetreden bij het ophalen van de hoofdstukken."
        );
      }
    };

    /* eslint-disable @typescript-eslint/no-floating-promises */
    fetchHoofdstukken();

    return () => {
      controller.abort();
      // Reset paged ready state when component unmounts
      setPagedReady(false);
    };
  }, [settings, bearerToken, params, setPagedReady, chapters, setHasBijlagen]);

  useLayoutEffect(() => {
    if (!rapportage || !contentRef.current) {
      return;
    }

    const isRendered = (): boolean => {
      if (!rapportage || !contentRef.current) {
        return false;
      }

      const content = contentRef.current;
      const hasVoorblad =
        !rapportage.voorblad || !!content.querySelector(".voorblad");
      const hasInleiding =
        !rapportage.inleiding || !!content.querySelector(".inleiding");

      return hasVoorblad && hasInleiding;
    };

    if (isRendered()) {
      // All required content is rendered
      setPagedReady(true);
    }
  }, [rapportage, setPagedReady]);

  // Separate useEffect to handle logo setting ONCE
  useEffect(() => {
    if (rapportage && !logoSetRef.current) {
      setLogo(rapportage.rapportLogo, "top-right");
      setFooterText(rapportage.rapportFooter);
      setHasFrontPage(!!rapportage.voorblad);
      logoSetRef.current = true;
    }
  }, [rapportage, setLogo, setFooterText, setHasFrontPage]);
  if (error) {
    console.error(error);
    return (
      <div className="rapportage-onderdelen rapportage-onderdelen--error">
        <p className="rapportage-onderdelen__error">{error}</p>
      </div>
    );
  }

  if (!rapportage) {
    return (
      <div className="rapportage-onderdelen rapportage-onderdelen--loading">
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div
      className="rapportage-onderdelen"
      data-testid="rapportage-onderdelen"
      id="rapportage-onderdelen"
    >
      <div id="render-content" ref={contentRef}>
        <>
          {componentOrder.map((key) => {
            if (key === "bijlagen" && !hasBijlagen) {
              return null;
            }

            const Component = rapportComponentMap[key];
            /* eslint-disable no-console */
            if (Component) {
              // Check if it will render by evaluating with the current rapportage
              const willRender = (() => {
                const result = Component({ rapport: rapportage });
                return result !== null;
              })();

              if (willRender) {
                console.log(
                  `Rendering component: ${key}`,
                  Component?.name || "Unnamed component"
                );
              }
            }
            return Component ? (
              <Component key={key} rapport={rapportage} />
            ) : null;
          })}
        </>
      </div>
    </div>
  );
};
